import {Dispatch} from "react";
import {
    UserHomepageComponent,
    UserHomepageComponentExpandability,
    UserHomepageContent,
    UserHomepageControllerEvents,
    UserHomepageControllerQueuedEvent,
    UserHomepageControlPanelEvents,
    UserHomepageDispatcherAction,
    UserHomepageEventEmitter,
    UserHomepageState,
    UserHomepageStateActions,
    UserHomepageTab,
    UserHomepageViewEvents,
    UserHomepageViewMode,
} from "../models";
import {EventEmitter} from 'eventemitter3';
import UserHomepageUtils from "./utils";
import {createUUId, deepCopy} from "../../../../core/services/utils/utils";

/**
 * @typedef {UserHomepageController} UserHomepageControllerApi
 */

/**
 * This interface manages the homepage components interactions and data manipulation.
 * All the interactions in the views and control panel of the user homepages must be done via this controller.
 */
class UserHomepageController extends EventEmitter {
    private readonly dispatch: Dispatch<UserHomepageDispatcherAction>;
    private readonly eventEmitterMapping = {
        [UserHomepageEventEmitter.controlPanel]: UserHomepageControlPanelEvents,
        [UserHomepageEventEmitter.view]: UserHomepageViewEvents,
    };
    private eventQueue: Array<UserHomepageControllerQueuedEvent>;
    private _state?: UserHomepageState;


    /**
     * Constructs a new controller for the user homepages.
     * @param {Dispatch<UserHomepageDispatcherAction>} dispatch
     */
    constructor(dispatch: Dispatch<UserHomepageDispatcherAction>) {
        super();
        this.dispatch = dispatch;
        this.eventQueue = [];
    }

    set state(value: UserHomepageState | undefined) {
        this._state = value;
        this.dispatchEvents();
    }

    // ############################         EVENT EMITTER         #############################

    /**
     * Dispatches all the queued events of this controller.
     *
     * * this method ideally only executes once the state of the reducer changes (through the setter of this
     * controller's state)
     */
    dispatchEvents(): void {
        const queue = deepCopy(this.eventQueue);
        for (const queuedEvent of queue) {
            this.emit(queuedEvent.event, ...queuedEvent.args);
        }
        const dispatchedIds = queue.map(e => e.id);
        this.eventQueue = this.eventQueue.filter(e => !dispatchedIds.includes(e.id));
    }

    /**
     * Queues an event to be dispatched
     * @param event
     * @param args
     * @private
     */
    private _queueEvent(event: string, ...args: any[]): void {
        this.eventQueue.push({
            id: createUUId(),
            event: event,
            args: Array.isArray(args)
                ? args
                : [args],
        });
    }

    /**
     * Calls each of the listeners registered for a given event.
     *
     * * if the emitter is specified, then emits an event for that specific emitter as well.
     * @param {T} event
     * @param {UserHomepageEventEmitter} emitter
     * @param {EventEmitter.EventArgs<any, T>} args
     */
    private async _emit(
        event: UserHomepageControllerEvents,
        emitter?: UserHomepageEventEmitter,
        ...args: EventEmitter.EventArgs<any, string>
    ): Promise<void> {

        const shouldQueue = this.state?.viewMode !== UserHomepageViewMode.preview;

        if (shouldQueue) this._queueEvent(event, emitter, ...args);
        else this.emit(event, emitter, ...args);

        if (!emitter)
            return;

        const emitterEvents = this.eventEmitterMapping[emitter];
        if (!emitterEvents)
            return;

        // @ts-ignore
        const _event = emitterEvents[event];
        if (!event)
            return;

        if (shouldQueue) this._queueEvent(_event, ...args);
        else this.emit(event, ...args);
    }


    // ############################         EXPANDABILITY         #############################

    /**
     * Opens the tab associated with the given tabId.
     *
     * * if the tabId is undefined, then closes all tabs.
     * @param {number?} tabId
     */
    openTab(tabId?: number): void {
        this.dispatch({
            type: UserHomepageStateActions.openTab,
            payload: {
                id: tabId,
            },
        })
    }

    /**
     * Opens the component associated with the given componentId.
     *
     * * if the componentId is undefined, then closes all components of the tab.
     * @param {number?} tabId
     * @param {number?} componentId
     * @param {UserHomepageComponentExpandability} mode
     */
    openComponent(
        tabId?: number,
        componentId?: number,
        mode: UserHomepageComponentExpandability = UserHomepageComponentExpandability.content,
    ): void {
        this.dispatch({
            type: UserHomepageStateActions.openComponent,
            payload: {
                tabId: tabId,
                id: componentId,
                mode: mode,
            },
        })
    }

    // ############################         STATE         #############################

    /**
     * Toggles the expanded state of the control panel.
     *
     * * fires an event that describes the new view mode of the controller.
     * @param {UserHomepageViewMode} viewMode
     */
    changeViewMode(viewMode: UserHomepageViewMode): void {
        this.dispatch({
            type: UserHomepageStateActions.changeViewMode,
            payload: viewMode,
        })
        this._emit(UserHomepageControllerEvents.onViewModeChanged, undefined, viewMode);
    }

    /**
     * Updates the title of the state.
     * @param {string} title
     * @param {boolean} commit
     */
    updateTitle(title: string, commit = false): void {
        this.dispatch({
            type: UserHomepageStateActions.updateTitle,
            payload: {
                title: title,
                commit: commit,
            },
        })
    }

    /**
     * Resets the state of this controller with the same values passed to the initial reduction's initializer.
     */
    resetState(): void {
        this.dispatch({
            type: UserHomepageStateActions.resetState,
            payload: undefined,
        })
    }

    /**
     * Sets the current data as the live data of the homepage edition of this controller.
     */
    setAsLive(): void {
        this.dispatch({
            type: UserHomepageStateActions.setAsLive,
            payload: undefined,
        })
    }

    // ############################         CONTROL PANEL         #############################

    /**
     * Toggles the expanded state of the control panel.
     * @param expanded
     */
    toggleControlPanelExpanded(expanded: boolean): void {
        this.dispatch({
            type: UserHomepageStateActions.toggleControlPanelCollapsed,
            payload: expanded,
        })
    }


    // ############################         LOADING DATA         #############################

    /**
     * Prepares the given homepage data to be used by this controller.
     *
     * * selects the first given tab as well if it exists.
     * @param {any} data
     * @param {any} config
     */
    loadHomepage(data: any, config: any): void {
        this.dispatch({
            type: UserHomepageStateActions.homepageLoaded,
            payload: {
                data: data,
                config: config,
            },
        })
        const firstTabId = data?.tabs.length ? data?.tabs[0].id : undefined;
        if (!firstTabId)
            return;
        this.selectTab(firstTabId);
    }

    /**
     * Loads the given components for the specified tab.
     *
     * @param {any} tab
     * @param {any} config
     */
    loadTab(tab: any, config: any): void {
        this.dispatch({
            type: UserHomepageStateActions.tabInfoLoaded,
            payload: {
                tab: tab,
                config: config,
            },
        })
    }

    // ############################         SELECTING DATA         #############################

    /**
     * Selects the tab associated with the given tabId.
     *
     *
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {UserHomepageEventEmitter?} emitter
     */
    selectTab(
        tabId?: number,
        emitter?: UserHomepageEventEmitter,
    ): void {
        this.dispatch({
            type: UserHomepageStateActions.selectTab,
            payload: {
                tabId: tabId,
            },
        })

        this.openTab(tabId);
        this._emit(UserHomepageControllerEvents.onTabSelected, emitter, tabId);
        if (this._state?.updatedData?.tabs?.find(e => e.id === tabId)?.loading) {
            this._emit(UserHomepageControllerEvents.onUnloadedTabSelected, emitter, tabId);
        }
    }


    /**
     * Selects the component associated with the given componentId.
     *
     *
     *
     *      - if the currently selected component is the same or the component does not exist, does nothing.
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {number} componentId
     * @param {UserHomepageEventEmitter?} emitter
     * @param {UserHomepageComponentExpandability?} mode
     */
    selectComponent(
        tabId: number,
        componentId: number,
        emitter?: UserHomepageEventEmitter,
        mode?: UserHomepageComponentExpandability,
    ): void {
        this.dispatch({
            type: UserHomepageStateActions.selectComponent,
            payload: {
                tabId: tabId,
                componentId: componentId,
            },
        })
        if (mode !== undefined) {
            this.openComponent(tabId, componentId, mode);
        }
        this._emit(UserHomepageControllerEvents.onComponentSelected, emitter, tabId, componentId);
    }

    /**
     * Selects the component based on the given proper arguments and then emits an event for selecting of the
     * search button.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {number} componentId
     * @param {UserHomepageEventEmitter} emitter
     */
    selectComponentSearchButton(
        tabId: number,
        componentId: number,
        emitter?: UserHomepageEventEmitter,
    ): void {
        this.selectComponent(tabId, componentId, emitter, UserHomepageComponentExpandability.content);
        this._emit(UserHomepageControllerEvents.onComponentSearchSelected, emitter, tabId, componentId);
    }

    /**
     * Selects the component associated with the given contentId.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {number} componentId
     * @param {number} contentId
     * @param {UserHomepageEventEmitter} emitter
     */
    selectContent(
        tabId: number,
        componentId: number,
        contentId: number,
        emitter?: UserHomepageEventEmitter,
    ): void {
        this.dispatch({
            type: UserHomepageStateActions.selectContent,
            payload: {
                tabId: tabId,
                componentId: componentId,
                contentId: contentId,
            },
        })
        this.openComponent(tabId, componentId, UserHomepageComponentExpandability.content);
        this._emit(UserHomepageControllerEvents.onContentSelected, emitter, tabId, componentId, contentId);
    }

    /**
     * Selects the given content given the proper arguments and then emits an event for clicking of the content.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {number} componentId
     * @param {number} contentId
     * @param {UserHomepageEventEmitter} emitter
     */
    clickContent(
        tabId: number,
        componentId: number,
        contentId: number,
        emitter?: UserHomepageEventEmitter,
    ): void {
        this.selectContent(tabId, componentId, contentId, emitter);
        this._emit(UserHomepageControllerEvents.onContentClicked, emitter, tabId, componentId, contentId);
    }

    /**
     * Selects the given content given the proper arguments and then emits an event for clicking of the content.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {number} componentId
     * @param {number} contentId
     * @param {number} tagId
     * @param {UserHomepageEventEmitter} emitter
     */
    selectProductTag(
        tabId: number,
        componentId: number,
        contentId: number,
        tagId: number,
        emitter?: UserHomepageEventEmitter,
    ): void {
        this.selectContent(tabId, componentId, contentId, emitter);
        this._emit(UserHomepageControllerEvents.onProductTagSelected, emitter, tabId, componentId, contentId, tagId);
    }

    // ############################        TAB CRUD         #############################

    /**
     * Creates a new tab for the data that this controller controls.
     *
     * * after creation, selects and opens the created tab
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {any} tab
     * @param {UserHomepageEventEmitter} emitter
     */
    createTab(
        tab: any,
        emitter: UserHomepageEventEmitter = UserHomepageEventEmitter.controlPanel,
    ): void {
        this.dispatch({
            type: UserHomepageStateActions.createTab,
            payload: tab,
        })
        this.selectTab(tab.id, emitter);
    }

    /**
     * Reorders the tabs based on the given tabs list.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {UserHomepageTab[]} tabs
     * @param {UserHomepageEventEmitter} emitter
     */
    reorderTabs(
        tabs: UserHomepageTab[],
        emitter: UserHomepageEventEmitter = UserHomepageEventEmitter.controlPanel
    ): void {
        this.dispatch({
            type: UserHomepageStateActions.reorderTabs,
            payload: {
                tabs: tabs,
            },
        })
    }

    /**
     * Updates the tab information of the state with the given tab.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {UserHomepageTab} tab
     * @param {boolean} commit
     * @param {UserHomepageEventEmitter} emitter
     */
    updateTab(
        tab: Partial<UserHomepageTab>,
        commit: boolean,
        emitter: UserHomepageEventEmitter = UserHomepageEventEmitter.controlPanel,
    ): void {
        this.dispatch({
            type: UserHomepageStateActions.updateTab,
            payload: {
                tab: tab,
                commit: commit,
            },
        })
        this.selectTab((tab.id as number), emitter);
    }

    /**
     * Removes the tab associated with the given tab id.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * * if the removed tab was the currently selected tab:
     *      - if there are other tabs in the state: select the one after or before it (based on its position)
     *      - else removes the selection
     * @param {number} tabId
     * @param {UserHomepageEventEmitter} emitter
     * @param {UserHomepageState} state
     */
    removeTab(
        tabId: number,
        emitter: UserHomepageEventEmitter = UserHomepageEventEmitter.controlPanel,
        state: UserHomepageState
    ): void {
        this.dispatch({
            type: UserHomepageStateActions.removeTab,
            payload: {
                id: tabId,
            },
        })

        if (!state || state.selection?.tabId !== tabId)
            return;

        const tabs = state.updatedData?.tabs ?? [];
        if (tabs?.length <= 1) {
            // remove selection
            this.selectTab(undefined, emitter);
        }

        const index = tabs?.findIndex(e => e.id === tabId) ?? -1;
        if (index === -1)
            return;

        // select previous tab if not first tab, otherwise select next tab
        let newSelectedTab = (tabs as UserHomepageTab[])[index + (index > 0 ? -1 : +1)];

        if (!!newSelectedTab) {
            this.selectTab((newSelectedTab.id as number), emitter);
        }
    }

    // ############################        COMPONENT CRUD         #############################

    /**
     * Creates a new component for given tab belonging to the data that this controller controls.
     *
     * * after creation, selects and opens the created component
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {any} component
     * @param {UserHomepageEventEmitter} emitter
     */
    createComponent(
        tabId: number,
        component: any,
        emitter?: UserHomepageEventEmitter,
    ): void {
        this.dispatch({
            type: UserHomepageStateActions.createComponent,
            payload: {
                tabId: tabId,
                data: component,
            },
        })
        this._emit(UserHomepageControllerEvents.onComponentCreated, emitter, tabId, component.id);
        this.selectComponent(
            tabId,
            component.id,
            emitter,
            UserHomepageComponentExpandability.content,
        )
    }

    /**
     * Reorders the components based on the given components list.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {UserHomepageComponent[]} components
     * @param {UserHomepageEventEmitter} emitter
     * @param {UserHomepageState} state
     */
    reorderComponents(
        tabId: number,
        components: UserHomepageComponent[],
        emitter: UserHomepageEventEmitter = UserHomepageEventEmitter.controlPanel,
        state?: UserHomepageState,
    ): void {
        // if (state) {
        //     if (!UserHomepageUtils.getSelection(state.updatedData, tabId)) {
        //         // could not find the given tab
        //         return;
        //     }
        // }
        this.dispatch({
            type: UserHomepageStateActions.reorderComponents,
            payload: {
                tabId: tabId,
                components: components,
            },
        })
    }

    /**
     * Updates the tab information of the component that is given to this method.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {UserHomepageComponent} component
     * @param {boolean} commit
     * @param {UserHomepageComponentExpandability} expandedMode
     * @param {UserHomepageEventEmitter} emitter
     * @param {UserHomepageState} state
     */
    updateComponent(
        tabId: number,
        component: Partial<UserHomepageComponent>,
        commit: boolean,
        expandedMode = UserHomepageComponentExpandability.content,
        emitter: UserHomepageEventEmitter = UserHomepageEventEmitter.controlPanel,
        state?: UserHomepageState
    ): void {
        // if (state) {
        //     if (!UserHomepageUtils.getSelection(state.updatedData, tabId, component.id))
        //         return;
        // }
        this.dispatch({
            type: UserHomepageStateActions.updateComponent,
            payload: {
                tabId: tabId,
                component: component,
                commit: commit,
            },
        })
        // select component so the view element gets focused
        this.selectComponent(tabId, component.id as number, emitter, expandedMode)
    }

    /**
     * Removes the component associated with the given component id.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * * if the removed component was the currently selected component:
     *      - if there are other components in the state: select the one after or before it (based on its position)
     *      - else removes the selection of the component.
     * @param {number} tabId
     * @param {number} componentId
     * @param {UserHomepageEventEmitter} emitter
     * @param {UserHomepageState} state
     */
    removeComponent(
        tabId: number,
        componentId: number,
        state: UserHomepageState,
        emitter: UserHomepageEventEmitter = UserHomepageEventEmitter.controlPanel,
    ): void {
        const component = UserHomepageUtils.getSelection(state.updatedData, tabId, componentId);
        if (!component)
            return;

        this.dispatch({
            type: UserHomepageStateActions.removeComponent,
            payload: {
                tabId: tabId,
                id: componentId,
            },
        })

        if (state.selection?.componentId !== tabId)
            return;

        const tab = UserHomepageUtils.getSelection(state.updatedData, tabId);

        if ((tab?.components?.length ?? 0) > 1) {
            const index = tab?.components?.findIndex(e => e.id === tabId) ?? -1;
            let newSelectedComponent: any = undefined;
            // select previous component if not first component, otherwise select next component
            if (index > 0) {
                newSelectedComponent = (tab?.components as UserHomepageComponent[])[index - 1];
                this.selectComponent(tabId, (newSelectedComponent.id as number), emitter, UserHomepageComponentExpandability.content);
            } else if (index === 0) {
                newSelectedComponent = (tab?.components as UserHomepageComponent[])[index + 1];
            }
            if (!!newSelectedComponent) {
                this.selectComponent(tabId, (newSelectedComponent.id as number), emitter, UserHomepageComponentExpandability.content);
            }
        } else {
            // remove selection
            this.dispatch({
                type: UserHomepageStateActions.selectComponent,
                payload: {
                    tabId: undefined,
                    componentId: undefined,
                },
            })
            this.openComponent(tabId, undefined);
        }
    }

    // ############################        CONTENT CRUD         #############################

    /**
     * Reorders the contents of a component based on the given contents list.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {number} componentId
     * @param {UserHomepageContent[]} content
     * @param {UserHomepageEventEmitter} emitter
     */
    reorderContent(
        tabId: number,
        componentId: number,
        content: UserHomepageContent[],
        emitter: UserHomepageEventEmitter = UserHomepageEventEmitter.controlPanel,
    ) {
        this.dispatch({
            type: UserHomepageStateActions.reorderContent,
            payload: {
                tabId: tabId,
                componentId: componentId,
                content: content,
            },
        })
    }

    /**
     * add the given content it to the contentList of the component based on the given arguments.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {number} componentId
     * @param {UserHomepageContent[]} content
     * @param {UserHomepageContent[]} commit
     * @param {UserHomepageEventEmitter} emitter
     */
    addContent(
        tabId: number,
        componentId: number,
        content: UserHomepageContent[],
        commit = false,
        emitter: UserHomepageEventEmitter = UserHomepageEventEmitter.controlPanel,
    ) {
        this.dispatch({
            type: UserHomepageStateActions.addContent,
            payload: {
                tabId: tabId,
                componentId: componentId,
                content: content,
                commit: commit,
            },
        })
    }

    /**
     * Removes given content to the contentList of the component based on the given arguments.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {number} componentId
     * @param {UserHomepageContent[]} contentId
     * @param {boolean} commit
     * @param {UserHomepageEventEmitter} emitter
     */
    removeContent(
        tabId: number,
        componentId: number,
        contentId: number,
        commit = false,
        emitter: UserHomepageEventEmitter = UserHomepageEventEmitter.controlPanel,
    ) {
        this.dispatch({
            type: UserHomepageStateActions.removeContent,
            payload: {
                tabId: tabId,
                componentId: componentId,
                contentId: contentId,
                commit: commit,
            },
        })
    }

    /**
     * Removes given content to the contentList of the component based on the given arguments.
     *
     * * if emitter is specified, only the event for that emitter is emitted, otherwise a general event is emitted.
     * @param {number} tabId
     * @param {number} componentId
     * @param {UserHomepageContent} content
     * @param {boolean} commit
     * @param {UserHomepageEventEmitter} emitter
     */
    updateContent(
        tabId: number,
        componentId: number,
        content: UserHomepageContent,
        commit = false,
        emitter: UserHomepageEventEmitter = UserHomepageEventEmitter.controlPanel,
    ) {
        this.dispatch({
            type: UserHomepageStateActions.updateContent,
            payload: {
                tabId: tabId,
                componentId: componentId,
                content: content,
                commit: commit,
            },
        })
    }

}

export default UserHomepageController;
